import React, { useMemo, useState } from "react";
import { useT } from "../i18n/LanguageContext";
import { BLOG_POSTS } from "../data/blogPosts";
import { Play, Clock, ShieldCheck, BrainCircuit, Database, Cpu, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORY_META = {
  cyber: { icon: ShieldCheck, color: "#1093f0" },
  ai: { icon: BrainCircuit, color: "#a855f7" },
  bigdata: { icon: Database, color: "#10b981" },
  it: { icon: Cpu, color: "#f59e0b" },
};

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(
      lang === "zh" ? "zh-CN" : lang === "es" ? "es-ES" : lang === "en" ? "en-GB" : "fr-FR",
      { year: "numeric", month: "short", day: "numeric" }
    );
  } catch {
    return iso;
  }
};

const Blog = () => {
  const { t, lang } = useT();
  const [category, setCategory] = useState("all");

  const filtered = useMemo(() => {
    return category === "all" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === category);
  }, [category]);

  const featured = useMemo(() => BLOG_POSTS.find((p) => p.featured), []);
  const videos = useMemo(() => BLOG_POSTS.filter((p) => p.youtube), []);

  const cats = [
    { id: "all", label: t("blog.all") },
    { id: "cyber", label: t("blog.categories.cyber") },
    { id: "ai", label: t("blog.categories.ai") },
    { id: "bigdata", label: t("blog.categories.bigdata") },
    { id: "it", label: t("blog.categories.it") },
  ];

  return (
    <div data-testid="blog-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hx-dot-grid opacity-40" />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1093f0]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12">
          <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-4">
            {t("blog.heroKicker")}
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-[#131517] leading-[1.02] max-w-4xl">
            {t("blog.heroTitle1")}<br />
            <span className="italic font-normal">{t("blog.heroTitle2")}</span>
          </h1>
          <p className="mt-8 text-lg text-[#3f444b] max-w-2xl">{t("blog.heroDesc")}</p>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-6">
            {t("blog.featured")}
          </div>
          <article
            className="group grid lg:grid-cols-12 gap-10 hx-card bg-white border border-black/8 rounded-3xl overflow-hidden p-0"
            data-testid={`blog-featured-${featured.id}`}
          >
            <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
              <img
                src={featured.image}
                alt=""
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <CategoryBadge category={featured.category} t={t} />
            </div>
            <div className="lg:col-span-5 p-10 flex flex-col justify-center">
              <div className="text-xs mono text-[#6b7079] mb-3">
                {formatDate(featured.date, lang)} · {featured.readTime} {t("blog.readTime")}
              </div>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#131517] leading-tight mb-4">
                {featured[lang]?.title || featured.fr.title}
              </h2>
              <p className="text-[#3f444b] mb-8 leading-relaxed">
                {featured[lang]?.excerpt || featured.fr.excerpt}
              </p>
              <button
                className="inline-flex items-center gap-2 text-[#131517] font-medium hx-link-underline self-start"
                data-testid={`blog-featured-cta-${featured.id}`}
              >
                {t("common.readArticle")} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </article>
        </section>
      )}

      {/* VIDEO TUTORIALS */}
      {videos.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-3">
                {t("blog.videoTutos")}
              </div>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#131517]">
                YouTube · {t("blog.videoTutos")}
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {videos.slice(0, 3).map((v) => (
              <VideoCard key={v.id} post={v} t={t} lang={lang} />
            ))}
          </div>
        </section>
      )}

      {/* ARTICLES + FILTERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-3">
              {t("blog.latest")}
            </div>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#131517]">
              {t("blog.latest")}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="blog-filters">
            {cats.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                data-testid={`blog-filter-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  category === c.id
                    ? "bg-[#131517] text-white border-[#131517]"
                    : "bg-white text-[#131517] border-black/10 hover:border-[#131517]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 text-center text-[#6b7079]" data-testid="blog-empty">
            {t("blog.noResults")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) =>
              p.youtube
                ? <VideoCard key={p.id} post={p} t={t} lang={lang} />
                : <ArticleCard key={p.id} post={p} t={t} lang={lang} />
            )}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <div className="rounded-3xl bg-[#131517] text-white p-12 lg:p-16 hx-grain relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#1093f0]/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <h2 className="text-3xl lg:text-5xl font-semibold leading-[1.05]">
                {t("sections.ctaTitle")}
              </h2>
              <p className="mt-4 text-[#9ca0a6] max-w-xl">{t("sections.ctaDesc")}</p>
            </div>
            <div className="lg:col-span-4">
              <Link
                to="/contact"
                className="hx-btn-primary inline-flex items-center justify-between gap-2 bg-[#1093f0] text-[#0a0e10] px-7 py-5 rounded-full text-sm font-semibold w-full"
                data-testid="blog-cta-contact"
              >
                {t("common.startProject")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const CategoryBadge = ({ category, t }) => {
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;
  return (
    <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-xs font-medium">
      <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
      {t(`blog.categories.${category}`)}
    </div>
  );
};

const ArticleCard = ({ post, t, lang }) => {
  const content = post[lang] || post.fr;
  const Wrapper = post.url ? "a" : "div";
  const wrapperProps = post.url
    ? { href: post.url, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className="group hx-card bg-white border border-black/8 rounded-3xl overflow-hidden flex flex-col"
      data-testid={`blog-card-${post.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={post.image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <CategoryBadge category={post.category} t={t} />
      </div>
      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-center gap-3 text-xs text-[#6b7079] mb-4 mono">
          <span>{formatDate(post.date, lang)}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime} {t("blog.readTime")}</span>
        </div>
        <h3 className="text-xl font-semibold text-[#131517] mb-3 leading-snug">{content.title}</h3>
        <p className="text-sm text-[#3f444b] leading-relaxed flex-1">{content.excerpt}</p>
        <div className="mt-6 pt-6 border-t border-black/5 flex items-center justify-between">
          <span className="text-xs mono text-[#1093f0]">{t(`blog.categories.${post.category}`)}</span>
          <ArrowUpRight className="w-4 h-4 text-[#1093f0]" />
        </div>
      </div>
    </Wrapper>
  );
};

const VideoCard = ({ post, t, lang }) => {
  const content = post[lang] || post.fr;
  const [playing, setPlaying] = useState(false);
  return (
    <article
      className="hx-card bg-white border border-black/8 rounded-3xl overflow-hidden flex flex-col"
      data-testid={`blog-video-${post.id}`}
    >
      <div className="relative aspect-video bg-black">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${post.youtube}?autoplay=1&rel=0`}
            title={content.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            data-testid={`blog-video-iframe-${post.id}`}
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="group w-full h-full relative"
            data-testid={`blog-video-play-${post.id}`}
            aria-label="play"
          >
            <img
              src={`https://img.youtube.com/vi/${post.youtube}/hqdefault.jpg`}
              alt=""
              className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-[#1093f0] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 text-[#0a0e10] fill-[#0a0e10] translate-x-0.5" />
              </div>
            </div>
            <CategoryBadge category={post.category} t={t} />
          </button>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-lg font-semibold text-[#131517] mb-2 leading-snug">{content.title}</h3>
        <p className="text-sm text-[#3f444b] leading-relaxed line-clamp-2">{content.excerpt}</p>
      </div>
    </article>
  );
};

export default Blog;
